import { api } from './api';
import { Movie, MovieDetail } from '../types/Movie';

export interface MovieSearchFilters {
  genres?: number[];
  countries?: string[];
  year?: number | '';
  order?: string;
}

export interface MovieGenre {
  genre_id: number;
  name: string;
  japanese_name: string | null;
}

export interface MovieCountry {
  iso_3166_1: string;
  name: string;
  japanese_name: string | null;
}

export const getMovieGenres = (): Promise<MovieGenre[]> => {
  return api.get('/films/movies/genres/').then(r => r.data);
};

export const getMovieCountries = (): Promise<MovieCountry[]> => {
  return api.get('/films/movies/countries/').then(r => r.data);
};

export const searchMovies = (
  query: string,
  page: number,
  filters: MovieSearchFilters = {}
): Promise<{ results: Movie[]; count: number }> => {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
  });

  if (filters.genres && filters.genres.length > 0) {
    params.append('genres', filters.genres.join(','));
  }
  if (filters.countries && filters.countries.length > 0) {
    params.append('countries', filters.countries.join(','));
  }
  if (filters.year) {
    params.append('year', filters.year.toString());
  }
  if (filters.order && filters.order !== '') {
    params.append('order', filters.order);
  }

  return api.get(`/films/movies/search/?${params.toString()}`).then(response => {
    return {
      results: response.data.results || [],
      count: response.data.count || 0,
    };
  });
};

export const getMovieDetails = (movieId: number): Promise<MovieDetail> => {
  return api.get(`/films/movies/${movieId}/`).then(response => response.data);
};

// ============================================================
// Movie Category API
// ============================================================

export const getMovieByGenre = (genreId: string, page: number = 1) => {
  return api.get(`/films/movies/by-genre/${genreId}/`, { params: { page } }).then(r => r.data);
};

export const getMovieByCompany = (companyId: string, page: number = 1) => {
  return api.get(`/films/movies/by-company/${companyId}/`, { params: { page } }).then(r => r.data);
};

export const getMovieByCollection = (collectionId: string, page: number = 1) => {
  return api.get(`/films/movies/by-collection/${collectionId}/`, { params: { page } }).then(r => r.data);
};

export const getMovieByPerson = (personId: string, page: number = 1) => {
  return api.get(`/films/movies/by-person/${personId}/`, { params: { page } }).then(r => r.data);
};

// ============================================================
// Movie Status API
// ============================================================

export const setMovieStatus = (movieId: number, status: number | null): Promise<any> => {
  return api.post('/films/user-movie-status/set_status/', { movie_id: movieId, status }).then(r => r.data);
};

export const setMovieReview = (movieId: number, data: import('../types/Movie').MovieReviewData): Promise<any> => {
  return api.post('/films/user-movie-status/set_review/', {
    movie_id: movieId,
    score: data.score,
    review_text: data.reviewText,
    score_tags: data.scoreTags.length > 0 ? data.scoreTags : null,
    score_story: data.scoreStory,
    score_visual: data.scoreVisual,
    score_sound: data.scoreSound,
    score_acting: data.scoreActing,
    score_direction: data.scoreDirection,
    watch_medium: data.watchMedium.length > 0 ? data.watchMedium : null,
    watch_style: data.watchStyle.length > 0 ? data.watchStyle : null,
    start_date: data.startDate,
    end_date: data.endDate,
    rewatch: data.rewatch,
  }).then(r => r.data);
};

export interface MovieStatusResponse {
  status: number | null;
  score: number | null;
  score_tags: number[] | null;
  score_story: number | null;
  score_visual: number | null;
  score_sound: number | null;
  score_acting: number | null;
  score_direction: number | null;
  watch_medium: number[] | null;
  watch_style: number[] | null;
  start_date: string | null;
  end_date: string | null;
  rewatch: boolean;
  review_text: string | null;
}

export const getMovieStatus = (movieId: number): Promise<MovieStatusResponse> => {
  return api.get('/films/user-movie-status/get_status/', { params: { movie_id: movieId } }).then(r => r.data);
};

export const getBasicMovieStatuses = (movieIds: number[]): Promise<Record<number, number>> => {
  return api.get('/films/user-movie-status/get_basic_statuses/', {
    params: { movie_ids: movieIds.join(',') }
  }).then(r => r.data);
};

// ============================================================
// Library API
// ============================================================

export const getMovieByStatus = (
  status: number,
  page?: number,
  query?: string,
  order?: string
) => api.get('/films/user-movie-status/by_status/', { params: { status, page, query, order } });

export const getMovieByStatusForUser = (
  username: string,
  status: number,
  page?: number,
  query?: string,
  order?: string
) => api.get(`/films/user-movie-status/by_status/${username}/`, { params: { status, page, query, order } });

export const getProfileMovieStatuses = (movieIds: number[]): Promise<Record<number, {
  status: number | null;
  score: number | null;
  review_text: string | null;
  start_date: string | null;
  end_date: string | null;
  sub_status: number[] | null;
  score_tags: number[] | null;
}>> => api.get('/films/user-movie-status/get_profile_statuses/', {
  params: { movie_ids: movieIds.join(',') }
}).then(r => r.data);

export const getPublicMovieStatuses = (username: string, movieIds: number[]): Promise<Record<number, {
  status: number | null;
  score: number | null;
  review_text: string | null;
  start_date: string | null;
  end_date: string | null;
  sub_status: number[] | null;
  score_tags: number[] | null;
}>> => api.get(`/films/user-movie-status/get_public_statuses/${username}/`, {
  params: { movie_ids: movieIds.join(',') }
}).then(r => r.data);

// ============================================================
// Movie Reviews API
// ============================================================

export interface MovieReviewDetail {
  id: number | string;
  movie_id: number;
  movie: {
    movie_id: number;
    title: string;
    japanese_name?: string | null;
    original_title?: string | null;
    poster_path?: string | null;
    release_date?: string | null;
    runtime?: number | null;
  };
  review_text: string;
  created_at: string;
  updated_at: string;
  score: number | null;
  score_tags: number[] | null;
  score_story: number | null;
  score_visual: number | null;
  score_sound: number | null;
  score_acting: number | null;
  score_direction: number | null;
  watch_medium: number[] | null;
  watch_style: number[] | null;
  start_date: string | null;
  end_date: string | null;
  like_count: number;
}

export const getMovieReviewsByUser = async (
  username: string,
  page: number = 1,
  query: string = '',
  order: string = 'default'
) => {
  const params = new URLSearchParams({ page: page.toString() });
  if (query) params.append('query', query);
  if (order !== 'default') params.append('order', order);
  const response = await api.get(`/films/movie-reviews/user/${username}/?${params.toString()}`);
  return response.data;
};
